import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { DestinationPicker } from "@/components/DestinationPicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EditableCell } from "@/components/EditableCell";
import { submitOrderWithOfflineFallback } from "@/utils/pendingOrderSync";
import { maybeCapturePosition } from "@/utils/geo";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { isWarehouseView } from "@/utils/permissions";
import {
  calcLineTotal as _calcLineTotal,
  calcNettoUnit as _calcNettoUnit,
  needsPriceWarning as _needsPriceWarning,
  calcPendingNetto,
} from "@/utils/orderPricing";
import { ClientHistoryPanel } from "./order/ClientHistoryPanel";
import {
  Menu,
  ArrowLeft,
  Search,
  Plus,
  Minus,
  Trash2,
  Save,
  X,
  Lock,
  Star,
  Tag,
  StickyNote,
  Gift,
  AlertTriangle,
} from "lucide-react";

export default function OrderEditor() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const isEdit = !!orderId;

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [showClientList, setShowClientList] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingQty, setPendingQty] = useState(1);
  const [pendingOmaggio, setPendingOmaggio] = useState(0);
  const [pendingPrice, setPendingPrice] = useState(0);
  const [pendingSc1, setPendingSc1] = useState("");
  const [pendingSc2, setPendingSc2] = useState("");
  const [pendingSc3, setPendingSc3] = useState("");
  const [pendingSc4, setPendingSc4] = useState("");
  const [noteOpenForIdx, setNoteOpenForIdx] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [clientPrices, setClientPrices] = useState({}); // product_id -> {price, source, sc1, sc2}
  const [giacenze, setGiacenze] = useState({}); // sku -> qty
  const [lastPrices, setLastPrices] = useState({}); // product_id -> {ultimo_prezzo, ultima_qta, ultima_data}
  const [clientScontiFissi, setClientScontiFissi] = useState(false);
  const [scontoCanale, setScontoCanale] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [form, setForm] = useState({
    client_id: null,
    client_name: "",
    client_partita_iva: "",
    client_indirizzo: "",
    client_citta: "",
    destination_id: null,
    destination_label: "",
    items: [],
    note: "",
  });

  // Snapshot del form al momento del caricamento (per detection "dirty")
  const initialSnapshotRef = useRef(JSON.stringify({ items: [], note: "" }));
  const justSavedRef = useRef(false);

  // L'ordine ha modifiche non salvate? Confronto sui campi più rilevanti.
  const isDirty = useMemo(() => {
    if (justSavedRef.current) return false;
    const current = JSON.stringify({ items: form.items, note: form.note });
    return current !== initialSnapshotRef.current;
  }, [form.items, form.note]);

  // Avvisa l'utente prima di uscire (back Android / chiusura tab) se ci sono modifiche
  useUnsavedChangesGuard(isDirty);

  const basePath = (() => {
    if (user?.role === "admin") return "/admin";
    if (isWarehouseView(user)) return "/warehouse";
    return "/agent";
  })();

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          authAxios.get("/clients"),
          authAxios.get("/products"),
        ]);
        setClients(cRes.data);
        setProducts(pRes.data);
        // Pre-load giacenze for all products (one batch call)
        const allSkus = (pRes.data || []).map(p => p.sku).filter(Boolean);
        if (allSkus.length > 0) {
          try {
            const gRes = await authAxios.post("/giacenze/by-skus", { skus: allSkus });
            const map = {};
            for (const [sku, v] of Object.entries(gRes.data.giacenze || {})) map[sku] = v.qty;
            setGiacenze(map);
          } catch (e) { console.debug("[OrderEditor] giacenze fetch failed (optional):", e?.message); }
        }
      } catch {
        toast.error("Errore nel caricamento dati");
      }
    };
    load();
  }, [authAxios]);

  useEffect(() => {
    if (isEdit) {
      const loadOrder = async () => {
        try {
          const res = await authAxios.get(`/orders/${orderId}`);
          const o = res.data;
          setForm({
            client_id: o.client_id,
            client_name: o.client_name,
            client_partita_iva: o.client_partita_iva || "",
            client_indirizzo: o.client_indirizzo || "",
            client_citta: o.client_citta || "",
            items: o.items.map((it) => ({
              product_id: it.product_id,
              product_name: it.product_name,
              product_sku: it.product_sku || "",
              quantity: it.quantity,
              qty_omaggio: it.qty_omaggio || 0,
              unit: it.unit || "pz",
              price_unit: it.price_unit,
              sc1: it.sc1 || 0,
              sc2: it.sc2 || 0,
              sc3: it.sc3 || 0,
              sc4: it.sc4 || 0,
              iva_percentage: it.iva_percentage || 10,
            })),
            note: o.note || "",
          });
          // Ripristina la destinazione scelta (serve all'export BBB)
          if (o.client_id) {
            const destRes = await authAxios
              .get(`/clients/${o.client_id}/destinations`)
              .catch(() => ({ data: [] }));
            const list = destRes.data || [];
            setDestinations(list);
            const d = o.cod_destin
              ? list.find((x) => String(x.codice_dest) === String(o.cod_destin))
              : null;
            if (d) {
              setForm((prev) => ({
                ...prev,
                destination_id: d.id,
                destination_label: `${d.indirizzo || ""}, ${d.citta || ""} ${d.cap || ""}`.trim(),
              }));
            }
          }
          // Aggiorna lo snapshot: ora l'ordine "caricato" è lo stato base.
          initialSnapshotRef.current = JSON.stringify({
            items: o.items.map((it) => ({
              product_id: it.product_id,
              product_name: it.product_name,
              product_sku: it.product_sku || "",
              quantity: it.quantity,
              qty_omaggio: it.qty_omaggio || 0,
              unit: it.unit || "pz",
              price_unit: it.price_unit,
              sc1: it.sc1 || 0,
              sc2: it.sc2 || 0,
              sc3: it.sc3 || 0,
              sc4: it.sc4 || 0,
              iva_percentage: it.iva_percentage || 10,
            })),
            note: o.note || "",
          });
          setClientSearch(o.client_name);
        } catch {
          toast.error("Ordine non trovato");
          navigate(`${basePath}/orders`);
        }
      };
      loadOrder();
    }
  }, [isEdit, orderId, authAxios, navigate, basePath]);

  const selectClient = async (c) => {
    setForm((prev) => ({
      ...prev,
      client_id: c.id,
      client_name: c.ragione_sociale,
      client_partita_iva: c.partita_iva || "",
      client_indirizzo: c.indirizzo || "",
      client_citta: c.citta || "",
      destination_id: null,
      destination_label: "",
    }));
    setClientSearch(c.ragione_sociale);
    setShowClientList(false);
    setClientScontiFissi(!!c.sconti_fissi);
    // Load destinations, prices and history in parallel
    setLoadingHistory(true);
    try {
      const allProductIds = products.map((p) => p.id);
      const [destRes, resolvedRes, lastRes, histRes] = await Promise.all([
        authAxios.get(`/clients/${c.id}/destinations`).catch(() => ({ data: [] })),
        allProductIds.length > 0
          ? authAxios.post(`/clients/${c.id}/resolve-prices`, { product_ids: allProductIds }).catch(() => ({ data: { prices: {} } }))
          : Promise.resolve({ data: { prices: {} } }),
        authAxios.get(`/clients/${c.id}/last-prices`).catch(() => ({ data: { prices: {} } })),
        authAxios.get(`/clients/${c.id}/history?limit=200`).catch(() => ({ data: { items: [] } })),
      ]);
      setDestinations(destRes.data);
      setClientPrices(resolvedRes.data.prices || {});
      setScontoCanale(resolvedRes.data.sconto_canale || 0);
      setLastPrices(lastRes.data.prices || {});
      setHistory(histRes.data.items || []);
    } catch {
      console.error("Failed to load destinations/prices");
      setDestinations([]);
      setClientPrices({});
      setLastPrices({});
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const selectDestination = (d) => {
    setForm((prev) => ({
      ...prev,
      destination_id: d.id,
      destination_label: `${d.indirizzo || ""}, ${d.citta || ""} ${d.cap || ""}`.trim(),
      client_indirizzo: d.indirizzo || prev.client_indirizzo,
      client_citta: d.citta || prev.client_citta,
    }));
  };

  const handleSedeLegale = () => {
    const client = clients.find(c => c.id === form.client_id);
    if (client) {
      setForm((prev) => ({
        ...prev,
        destination_id: null,
        destination_label: "Sede legale",
        client_indirizzo: client.indirizzo || "",
        client_citta: client.citta || "",
      }));
    }
  };

  const selectProduct = (p) => {
    setPendingProduct(p);
    setPendingQty(1);
    setPendingOmaggio(0);
    const resolved = clientPrices[p.id];
    if (resolved?.source === "manual") {
      setPendingPrice(resolved.price);
      setPendingSc1("");
      setPendingSc2("");
      setPendingSc3("");
      setPendingSc4("");
    } else if (resolved?.source === "promo" && resolved?.promo_net_fixed) {
      // Promo a netto fisso: usiamo come price_unit, sconti azzerati
      setPendingPrice(resolved.price);
      setPendingSc1("");
      setPendingSc2("");
      setPendingSc3("");
      setPendingSc4("");
    } else if (resolved?.source === "rule" || resolved?.source === "promo") {
      setPendingPrice(p.price_list || 0);
      setPendingSc1(resolved.sc1 > 0 ? String(resolved.sc1) : "");
      setPendingSc2(resolved.sc2 > 0 ? String(resolved.sc2) : "");
      setPendingSc3(resolved.sc3 > 0 ? String(resolved.sc3) : "");
      setPendingSc4(resolved.sc4 > 0 ? String(resolved.sc4) : "");
    } else {
      setPendingPrice(p.price_list || 0);
      setPendingSc1("");
      setPendingSc2("");
      setPendingSc3("");
      setPendingSc4("");
    }
    setProductSearch("");
    setShowProductList(false);
  };

  const confirmAddProduct = () => {
    if (!pendingProduct) return;
    const p = pendingProduct;
    const qty = parseFloat(pendingQty) || 0;
    const qtyOm = parseFloat(pendingOmaggio) || 0;
    const price = parseFloat(pendingPrice) || p.price_list || 0;
    const sc1 = parseFloat(pendingSc1) || 0;
    const sc2 = parseFloat(pendingSc2) || 0;
    const sc3 = parseFloat(pendingSc3) || 0;
    const sc4 = parseFloat(pendingSc4) || 0;
    const exists = form.items.find((it) => it.product_id === p.id);
    if (exists) {
      setForm((prev) => ({
        ...prev,
        items: prev.items.map((it) =>
          it.product_id === p.id
            ? {
                ...it,
                quantity: (parseFloat(it.quantity) || 0) + qty,
                qty_omaggio: (parseFloat(it.qty_omaggio) || 0) + qtyOm,
                price_unit: price,
                sc1, sc2, sc3, sc4,
              }
            : it
        ),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            product_id: p.id,
            product_name: p.name,
            product_sku: p.sku || "",
            quantity: qty,
            qty_omaggio: qtyOm,
            unit: p.unit_of_measure || "pz",
            price_unit: price,
            sc1, sc2, sc3, sc4,
            iva_percentage: p.iva_percentage || 10,
          },
        ],
      }));
    }
    setPendingProduct(null);
    toast.success(`${p.name} aggiunto`);
  };

  const updateItem = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) =>
        i === idx ? { ...it, [field]: value } : it
      ),
    }));
  };

  const removeItem = (idx) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  // Line total = qty * price * (1-sc1%) * (1-sc2%) * (1-sc3%) * (1-sc4%) * (1-scCanale%)
  const calcLineTotal = (item) => _calcLineTotal(item, scontoCanale);
  const calcNettoUnit = (item) => _calcNettoUnit(item, scontoCanale);

  // Avviso non bloccante "articolo senza prezzo deciso" — vedi utils/orderPricing.js
  const needsPriceWarning = (item) =>
    _needsPriceWarning(item, { products, lastPrices, scontoCanale, clientScontiFissi });

  const noPriceCount = (form?.items || []).reduce(
    (acc, it) => acc + (needsPriceWarning(it) ? 1 : 0),
    0,
  );

  const pendingNetto = calcPendingNetto(
    { pendingPrice, pendingSc1, pendingSc2, pendingSc3, pendingSc4 },
    scontoCanale,
  );

  const subtotalMerce = form.items.reduce((sum, it) => sum + calcLineTotal(it), 0);
  const ivaTotal = form.items.reduce((sum, it) => {
    return sum + calcLineTotal(it) * (it.iva_percentage || 0) / 100;
  }, 0);
  const total = Math.round((subtotalMerce + ivaTotal) * 100) / 100;

  const filteredClients = clients.filter(
    (c) =>
      c.ragione_sociale?.toLowerCase().includes(clientSearch.toLowerCase()) ||
      c.partita_iva?.includes(clientSearch) ||
      c.codice_cli?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredProducts = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.sku || "").toLowerCase().includes(productSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!form.client_name) {
      toast.error("Seleziona un cliente");
      return;
    }
    if (form.items.length === 0) {
      toast.error("Aggiungi almeno un prodotto");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        client_name: form.client_name,
        client_partita_iva: form.client_partita_iva || null,
        client_indirizzo: form.client_indirizzo || null,
        client_citta: form.client_citta || null,
        destination_id: form.destination_id || null,
        dest_id: form.destination_id || null,
        destination_label: form.destination_label || null,
        items: form.items.map((it) => ({
          product_id: it.product_id,
          product_name: it.product_name,
          product_sku: it.product_sku,
          quantity: parseFloat(it.quantity) || 0,
          qty_omaggio: parseFloat(it.qty_omaggio) || 0,
          unit: it.unit,
          price_unit: parseFloat(it.price_unit) || 0,
          sc1: parseFloat(it.sc1) || 0,
          sc2: parseFloat(it.sc2) || 0,
          sc3: parseFloat(it.sc3) || 0,
          sc4: parseFloat(it.sc4) || 0,
          iva_percentage: it.iva_percentage,
        })),
        sconto_canale: scontoCanale || 0,
        note: form.note || null,
      };
      if (isEdit) {
        await authAxios.put(`/orders/${orderId}`, payload);
        toast.success("Ordine aggiornato");
      } else {
        const geo = await maybeCapturePosition(user);
        const result = await submitOrderWithOfflineFallback(authAxios, { ...payload, ...(geo || {}) }, { source: "order_editor" });
        if (result.online) {
          toast.success("Ordine creato");
        } else {
          toast.success("Ordine salvato offline — verrà inviato automaticamente quando torni online", { duration: 5000 });
        }
      }
      // Auto-save client prices DISABILITATO (richiesta utente 2026-04-22)
      // Lavoriamo solo con i client_prices ufficiali importati. Per riattivare, rimuovere il commento.
      // if (form.client_id && form.items.length > 0) {
      //   try {
      //     await authAxios.post(`/clients/${form.client_id}/prices/bulk`, {
      //       items: form.items.map((it) => {
      //         let eff = parseFloat(it.price_unit) || 0;
      //         for (const s of [it.sc1, it.sc2, it.sc3, it.sc4]) {
      //           const v = parseFloat(s) || 0;
      //           if (v > 0) eff *= (1 - v / 100);
      //         }
      //         return { product_id: it.product_id, custom_price: Math.round(eff * 100) / 100 };
      //       }),
      //     });
      //   } catch (e) {
      //     console.error("Failed to bulk-save client prices:", e);
      //   }
      // }
      // Disabilita il guard "modifiche non salvate" prima di navigare via
      justSavedRef.current = true;
      navigate(`${basePath}/orders`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="order-editor">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Ordine" />
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate(`${basePath}/orders`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ordini
          </Button>
          <h1 className="font-heading text-xl font-bold text-white">
            {isEdit ? "Modifica Ordine" : "Nuovo Ordine"}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Client + Products */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <Card className="bg-[#18181b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Cliente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="client-search"
                    placeholder="Cerca cliente per nome o P.IVA..."
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientList(true);
                    }}
                    onFocus={() => setShowClientList(true)}
                    className="pl-10 bg-[#09090b] border-white/10 text-white placeholder:text-zinc-500"
                  />
                  {showClientList && clientSearch && (
                    <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-[#1c1c1f] border border-white/10 rounded-lg max-h-48 overflow-y-auto">
                      {filteredClients.slice(0, 20).map((c) => (
                        <button
                          key={c.id}
                          className="w-full text-left px-4 py-2 hover:bg-white/5 text-sm"
                          onClick={() => selectClient(c)}
                        >
                          <span className="text-white">{c.ragione_sociale}</span>
                          {c.partita_iva && (
                            <span className="text-zinc-500 ml-2">P.IVA: {c.partita_iva}</span>
                          )}
                        </button>
                      ))}
                      {filteredClients.length === 0 && (
                        <p className="px-4 py-2 text-zinc-500 text-sm">Nessun cliente trovato</p>
                      )}
                    </div>
                  )}
                </div>
                {form.client_name && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <span className="text-amber-400 text-sm font-medium flex-1">{form.client_name}</span>
                    {clientScontiFissi && (
                      <span className="text-red-400 text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Fissi</span>
                    )}
                    {Object.values(clientPrices).filter(v => v.source !== "none").length > 0 && (
                      <span className="text-emerald-400 text-xs flex items-center gap-1"><Star className="w-3 h-3" /> {Object.values(clientPrices).filter(v => v.source !== "none").length} sconti</span>
                    )}
                    {scontoCanale > 0 && (
                      <span className="text-cyan-400 text-xs">Canale: {scontoCanale}%</span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-zinc-400 hover:text-red-400"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, client_id: null, client_name: "", client_partita_iva: "", client_indirizzo: "", client_citta: "" }));
                        setClientSearch("");
                        setClientPrices({});
                        setClientScontiFissi(false);
                        setScontoCanale(0);
                      }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Destination Selector */}
            <DestinationPicker
              clientId={form.client_id}
              clientAddress={clients.find(c => c.id === form.client_id)?.indirizzo || ""}
              selectedDest={destinations.find(d => d.id === form.destination_id) || null}
              onSelect={(dest) => {
                if (dest) {
                  selectDestination(dest);
                } else {
                  handleSedeLegale();
                }
              }}
              destinations={destinations}
            />

            {/* Product Search + Items */}
            <Card className="bg-[#18181b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Prodotti</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {!pendingProduct ? (
                  <div className="relative order-2 sticky bottom-2 sm:static z-30">
                    <div className="bg-[#18181b]/95 backdrop-blur supports-[backdrop-filter]:bg-[#18181b]/80 border border-white/10 rounded-lg p-2 sm:p-0 sm:border-0 sm:bg-transparent sm:backdrop-blur-none">
                      {/* History toggle: only if a client is selected and we have at least one item */}
                      {form.client_id && (
                        <div className="mb-2">
                          <button
                            type="button"
                            data-testid="toggle-client-history-btn"
                            onClick={() => setShowHistory((s) => !s)}
                            className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
                          >
                            🕐 {showHistory ? "Nascondi" : "Mostra"} storico cliente
                            {history.length > 0 && !showHistory && (
                              <span className="text-zinc-500">({history.length})</span>
                            )}
                          </button>
                          {showHistory && (
                            <ClientHistoryPanel
                              loadingHistory={loadingHistory}
                              history={history}
                              formItems={form.items}
                              clientScontiFissi={clientScontiFissi}
                              onPickProduct={(h) => {
                                selectProduct(h);
                                setPendingQty(1);
                                setShowHistory(false);
                              }}
                            />
                          )}
                        </div>
                      )}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          data-testid="product-search"
                          placeholder="Cerca prodotto per nome o codice..."
                          value={productSearch}
                          onChange={(e) => {
                            setProductSearch(e.target.value);
                            setShowProductList(true);
                          }}
                          onFocus={() => productSearch && setShowProductList(true)}
                          className="pl-10 bg-[#09090b] border-white/10 text-white placeholder:text-zinc-500"
                        />
                        {showProductList && productSearch && (
                          <div className="absolute z-40 left-0 right-0 bg-[#1c1c1f] border border-white/10 rounded-lg max-h-60 overflow-y-auto bottom-full mb-1 sm:bottom-auto sm:top-full sm:mt-1 sm:mb-0">
                            {filteredProducts.slice(0, 30).map((p) => (
                              <button
                                key={p.id}
                                className="w-full text-left px-4 py-2.5 hover:bg-white/5 text-sm flex items-center justify-between"
                                onClick={() => selectProduct(p)}
                              >
                                <div>
                                  <span className="text-white">{p.name}</span>
                                  {p.sku && <span className="text-zinc-500 ml-2">({p.sku})</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                  {giacenze[p.sku] !== undefined && (
                                    <span
                                      className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${
                                        giacenze[p.sku] > 0
                                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                          : "bg-red-500/10 text-red-300 border-red-500/30"
                                      }`}
                                      title="Giacenza"
                                    >
                                      Giac. {Number(giacenze[p.sku]).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
                                    </span>
                                  )}
                                  {clientPrices[p.id]?.source === "manual" && (
                                    <span className="text-amber-400 font-mono text-xs font-bold">
                                      {clientPrices[p.id].price.toFixed(2)}
                                    </span>
                                  )}
                                  {clientPrices[p.id]?.source === "rule" && (
                                    (() => {
                                      const r = clientPrices[p.id];
                                      const parts = [r.sc1, r.sc2, r.sc3, r.sc4]
                                        .map((v) => parseFloat(v) || 0)
                                        .filter((v) => v > 0)
                                        .map((v) => (v % 1 === 0 ? String(v) : String(v).replace(".", ",")));
                                      if (parts.length === 0) return null;
                                      return (
                                        <span className="text-emerald-400 font-mono text-xs">
                                          -{parts.join("/-")}%
                                        </span>
                                      );
                                    })()
                                  )}
                                  <span className="text-zinc-400 font-mono text-xs">
                                    {(p.price_list || 0).toFixed(2)}
                                  </span>
                                </div>
                              </button>
                            ))}
                            {filteredProducts.length === 0 && (
                              <p className="px-4 py-2 text-zinc-500 text-sm">Nessun prodotto trovato</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#09090b] rounded-lg border border-amber-500/20 p-4 order-2" data-testid="pending-product-panel">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{pendingProduct.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {pendingProduct.sku && <span className="text-zinc-500 text-xs font-mono">{pendingProduct.sku}</span>}
                          <span className="text-zinc-500 text-xs">IVA: {pendingProduct.iva_percentage || 10}%</span>
                          <span className="text-zinc-500 text-xs">U.M.: {pendingProduct.unit_of_measure || "pz"}</span>
                          {(() => {
                            const r = clientPrices[pendingProduct.id];
                            if (!r || r.source !== "rule") return null;
                            const parts = [r.sc1, r.sc2, r.sc3, r.sc4]
                              .map((v) => parseFloat(v) || 0)
                              .filter((v) => v > 0);
                            if (parts.length === 0) return null;
                            return (
                              <span className="text-emerald-400 text-xs font-medium flex items-center gap-0.5">
                                <Star className="w-3 h-3" /> Sc. {parts.join("+")}%
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-zinc-400 hover:text-red-400"
                        onClick={() => setPendingProduct(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className="text-xs text-zinc-400 mb-1 block">Qtà</label>
                        <div className="flex items-center gap-1">
                          <Input
                            data-testid="pending-qty"
                            type="number"
                            value={pendingQty}
                            onChange={(e) => setPendingQty(e.target.value)}
                            className="min-w-0 text-center bg-[#18181b] border-white/10 text-white h-9 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                            step="0.1"
                            min="0.1"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-pink-400 mb-1 block flex items-center gap-1">
                          <Gift className="w-3 h-3" /> Om.
                        </label>
                        <Input
                          data-testid="pending-omaggio"
                          type="number"
                          value={pendingOmaggio}
                          onChange={(e) => setPendingOmaggio(e.target.value)}
                          className="min-w-0 text-center bg-[#18181b] border-pink-500/30 text-pink-300 h-9 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                          step="0.1"
                          min="0"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-400 mb-1 block">Prezzo</label>
                        <EditableCell
                          data-testid="pending-price"
                          value={pendingPrice} decimals={2}
                          onChange={(v) => setPendingPrice(v)}
                          disabled={clientScontiFissi}
                          className="w-full bg-[#18181b] border-white/10 text-white h-9 text-sm disabled:opacity-60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-emerald-400 mb-1 block">Sc.1 %</label>
                        <EditableCell
                          data-testid="pending-sc1"
                          value={pendingSc1} decimals={1} placeholder="-"
                          onChange={(v) => setPendingSc1(v)}
                          disabled={clientScontiFissi}
                          className="w-full bg-[#18181b] border-emerald-500/30 text-emerald-400 h-9 text-sm text-center disabled:opacity-60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-emerald-400 mb-1 block">Sc.2 %</label>
                        <EditableCell
                          data-testid="pending-sc2"
                          value={pendingSc2} decimals={1} placeholder="-"
                          onChange={(v) => setPendingSc2(v)}
                          disabled={clientScontiFissi}
                          className="w-full bg-[#18181b] border-emerald-500/30 text-emerald-400 h-9 text-sm text-center disabled:opacity-60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-emerald-400 mb-1 block">Sc.3 %</label>
                        <EditableCell
                          data-testid="pending-sc3"
                          value={pendingSc3} decimals={1} placeholder="-"
                          onChange={(v) => setPendingSc3(v)}
                          disabled={clientScontiFissi}
                          className="w-full bg-[#18181b] border-emerald-500/30 text-emerald-400 h-9 text-sm text-center disabled:opacity-60"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-emerald-400 mb-1 block">Sc.4 %</label>
                        <EditableCell
                          data-testid="pending-sc4"
                          value={pendingSc4} decimals={1} placeholder="-"
                          onChange={(v) => setPendingSc4(v)}
                          disabled={clientScontiFissi}
                          className="w-full bg-[#18181b] border-emerald-500/30 text-emerald-400 h-9 text-sm text-center disabled:opacity-60"
                        />
                      </div>
                      {scontoCanale > 0 && (
                        <div>
                          <label className="text-xs text-cyan-400 mb-1 block">Sc.Canale</label>
                          <div className="w-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 h-9 text-sm text-center rounded-md flex items-center justify-center font-mono font-semibold">
                            {scontoCanale}%
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="text-xs text-amber-400 mb-1 block">Netto unit.</label>
                        <EditableCell
                          data-testid="pending-netto"
                          value={pendingNetto || 0} decimals={3}
                          onChange={(v) => {
                            const netto = parseFloat(v) || 0;
                            const listino = parseFloat(pendingPrice) || 0;
                            if (netto <= 0 || listino <= 0) return;
                            const denom = listino * (1 - (scontoCanale || 0) / 100);
                            if (denom <= 0) return;
                            let sc = (1 - netto / denom) * 100;
                            sc = Math.max(0, Math.min(100, Math.round(sc * 100) / 100));
                            setPendingSc1(sc > 0 ? String(sc) : "");
                            setPendingSc2("");
                            setPendingSc3("");
                            setPendingSc4("");
                          }}
                          disabled={clientScontiFissi}
                          className="w-full bg-amber-500/10 border-amber-500/40 text-amber-300 h-9 text-sm text-right font-mono font-bold disabled:opacity-60"
                        />
                      </div>
                      {/* Quick-fill da price_min / price_auth / price_promo (solo non-Triolo) */}
                      {pendingProduct?.macro_category !== "Prodotti Triolo" && !clientScontiFissi && (
                        (() => {
                          const applyTargetNetto = (target) => {
                            const listino = parseFloat(pendingPrice) || 0;
                            if (!target || listino <= 0) return;
                            const denom = listino * (1 - (scontoCanale || 0) / 100);
                            if (denom <= 0) return;
                            let sc = (1 - target / denom) * 100;
                            sc = Math.max(0, Math.min(100, Math.round(sc * 100) / 100));
                            setPendingSc1(sc > 0 ? String(sc) : "");
                            setPendingSc2("");
                            setPendingSc3("");
                            setPendingSc4("");
                          };
                          const pMin = Number(pendingProduct?.price_min) || 0;
                          const pAuth = Number(pendingProduct?.price_auth) || 0;
                          const pPromo = Number(pendingProduct?.price_promo) || 0;
                          const pLast = Number(lastPrices[pendingProduct?.id]?.ultimo_prezzo) || 0;
                          const lastDate = (lastPrices[pendingProduct?.id]?.ultima_data || "").slice(0, 10);
                          if (pMin <= 0 && pAuth <= 0 && pPromo <= 0 && pLast <= 0) return null;
                          return (
                            <div className={scontoCanale > 0 ? "col-span-2 flex flex-wrap gap-2 items-end" : "col-span-3 flex flex-wrap gap-2 items-end"}>
                              {pMin > 0 && (
                                <button
                                  type="button"
                                  onClick={() => applyTargetNetto(pMin)}
                                  className="h-9 px-3 rounded-md border border-sky-500/40 bg-sky-500/10 text-sky-300 text-xs font-mono hover:bg-sky-500/20 transition-colors shrink-0"
                                  data-testid="pending-quick-min"
                                  title={`Applica prezzo minimo € ${pMin.toFixed(2)}`}
                                >
                                  Min € {pMin.toFixed(2)}
                                </button>
                              )}
                              {pAuth > 0 && (
                                <button
                                  type="button"
                                  onClick={() => applyTargetNetto(pAuth)}
                                  className="h-9 px-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs font-mono hover:bg-amber-500/20 transition-colors shrink-0"
                                  data-testid="pending-quick-auth"
                                  title={`Applica prezzo da autorizzare € ${pAuth.toFixed(2)}`}
                                >
                                  Aut € {pAuth.toFixed(2)}
                                </button>
                              )}
                              {pPromo > 0 && (
                                <button
                                  type="button"
                                  onClick={() => applyTargetNetto(pPromo)}
                                  className="h-9 px-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-xs font-mono hover:bg-red-500/20 transition-colors shrink-0 flex items-center gap-1"
                                  data-testid="pending-quick-promo"
                                  title={`Applica prezzo promo € ${pPromo.toFixed(2)}`}
                                >
                                  <Tag className="w-3 h-3" />
                                  Promo € {pPromo.toFixed(2)}
                                </button>
                              )}
                              {pLast > 0 && (
                                <button
                                  type="button"
                                  onClick={() => applyTargetNetto(pLast)}
                                  className="h-9 px-3 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300 text-xs font-mono hover:bg-fuchsia-500/20 transition-colors shrink-0"
                                  data-testid="pending-quick-last"
                                  title={`Ultimo prezzo praticato al cliente € ${pLast.toFixed(3)}${lastDate ? ` (${lastDate})` : ""}`}
                                >
                                  Ult. € {pLast.toFixed(3)}
                                </button>
                              )}
                            </div>
                          );
                        })()
                      )}
                      <div className="col-span-4">
                        <Button
                          data-testid="confirm-add-product"
                          className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium h-9"
                          onClick={confirmAddProduct}
                          disabled={(parseFloat(pendingQty) || 0) <= 0}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Aggiungi
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Items Table */}
                {form.items.length > 0 && (
                  <div className="order-1">
                    {noPriceCount > 0 && (
                      <div
                        className="mb-3 p-2.5 rounded-lg border border-red-500/40 bg-red-500/10 flex items-center gap-2"
                        data-testid="order-no-price-banner"
                      >
                        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-red-300 text-xs font-semibold">
                            {noPriceCount === 1
                              ? "1 articolo senza prezzo indicato"
                              : `${noPriceCount} articoli senza prezzo indicato`}
                          </p>
                          <p className="text-red-200/80 text-[10px]">
                            Imposta sconti o prezzo manuale per favorire la fatturazione.
                          </p>
                        </div>
                      </div>
                    )}
                    <table className="w-full text-sm table-fixed">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-zinc-400 py-2 font-medium text-xs w-[36%]">Prodotto</th>
                          <th className="text-center text-zinc-400 py-2 font-medium text-xs w-[12%]">Qtà</th>
                          <th className="text-right text-zinc-400 py-2 font-medium text-xs w-[14%]">Prezzo</th>
                          <th className="text-right text-zinc-400 py-2 font-medium text-xs w-[14%]">Netto</th>
                          <th className="text-right text-zinc-400 py-2 font-medium text-xs w-[14%]">Tot.</th>
                          <th className="w-[10%]"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((item, idx) => {
                          const lineTotal = calcLineTotal(item);
                          const nettoUnit = calcNettoUnit(item);
                          const isLocked = clientScontiFissi;
                          const noPrice = needsPriceWarning(item);
                          const rowBg = noPrice
                            ? "bg-red-500/10 outline outline-1 outline-red-500/40"
                            : (idx % 2 === 1 ? "bg-white/[0.025]" : "");
                          return (
                          <React.Fragment key={item.product_id || idx}>
                          <tr className={`border-b border-white/5 border-b-0 ${rowBg}`} data-testid={`order-item-${idx}`}>
                            <td className="pt-2 pb-0">
                              <p className="text-white text-xs">{item.product_name}</p>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {item.product_sku && <p className="text-zinc-500 text-[10px]">{item.product_sku}</p>}
                                {item.product_sku && giacenze[item.product_sku] !== undefined && (
                                  <span
                                    data-testid={`order-item-giacenza-${idx}`}
                                    className={`font-mono text-[9px] px-1 rounded border ${
                                      giacenze[item.product_sku] > 0
                                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                        : "bg-red-500/10 text-red-300 border-red-500/30"
                                    }`}
                                  >
                                    Giac. {Number(giacenze[item.product_sku]).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
                                  </span>
                                )}
                                {noPrice && (
                                  <span
                                    className="font-mono text-[9px] px-1 rounded border bg-red-500/15 text-red-200 border-red-500/40 font-bold"
                                    title="Imposta sconti o prezzo manuale prima di salvare l'ordine"
                                    data-testid={`order-item-no-price-${idx}`}
                                  >
                                    ⚠️ PREZZO
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="pt-2 pb-0">
                              <EditableCell value={item.quantity} decimals={3}
                                onChange={(v) => updateItem(idx, "quantity", v)}
                                className="w-full text-center bg-[#09090b] border-white/10 text-white h-7 text-xs" />
                            </td>
                            <td className="pt-2 pb-0">
                              <EditableCell value={item.price_unit} decimals={2}
                                onChange={(v) => updateItem(idx, "price_unit", v)}
                                disabled={isLocked}
                                className="w-full text-right bg-[#09090b] border-white/10 text-white h-7 text-xs disabled:opacity-60" />
                            </td>
                            <td className="pt-2 pb-0 text-right" data-testid={`order-item-netto-${idx}`}>
                              <EditableCell
                                value={nettoUnit} decimals={3}
                                onChange={(v) => {
                                  const netto = parseFloat(v) || 0;
                                  const listino = parseFloat(item.price_unit) || 0;
                                  if (netto <= 0 || listino <= 0) return;
                                  const denom = listino * (1 - (scontoCanale || 0) / 100);
                                  if (denom <= 0) return;
                                  let sc = (1 - netto / denom) * 100;
                                  sc = Math.max(0, Math.min(100, Math.round(sc * 100) / 100));
                                  setForm((prev) => ({
                                    ...prev,
                                    items: prev.items.map((it, i) =>
                                      i === idx ? { ...it, sc1: sc, sc2: 0, sc3: 0, sc4: 0 } : it
                                    ),
                                  }));
                                }}
                                disabled={isLocked}
                                className="w-full text-right bg-amber-500/10 border border-amber-500/30 text-amber-300 h-7 text-xs font-mono font-bold disabled:opacity-60"
                              />
                            </td>
                            <td className="pt-2 pb-0 text-right text-white font-mono text-xs font-medium">
                              {lineTotal.toFixed(2)}
                            </td>
                            <td className="pt-2 pb-0">
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500 hover:text-red-400"
                                onClick={() => removeItem(idx)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </td>
                          </tr>
                          <tr className={`border-b border-white/5 ${rowBg}`}>
                            <td colSpan={6} className="pb-2 pt-1 px-1">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-emerald-500/70 text-[10px] w-6">Sc%</span>
                                <EditableCell value={item.sc1} decimals={1} placeholder="-"
                                  onChange={(v) => updateItem(idx, "sc1", v)} disabled={isLocked}
                                  className="w-12 text-center bg-[#09090b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                                <span className="text-zinc-600 text-[10px]">+</span>
                                <EditableCell value={item.sc2} decimals={1} placeholder="-"
                                  onChange={(v) => updateItem(idx, "sc2", v)} disabled={isLocked}
                                  className="w-12 text-center bg-[#09090b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                                <span className="text-zinc-600 text-[10px]">+</span>
                                <EditableCell value={item.sc3} decimals={1} placeholder="-"
                                  onChange={(v) => updateItem(idx, "sc3", v)} disabled={isLocked}
                                  className="w-12 text-center bg-[#09090b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                                <span className="text-zinc-600 text-[10px]">+</span>
                                <EditableCell value={item.sc4} decimals={1} placeholder="-"
                                  onChange={(v) => updateItem(idx, "sc4", v)} disabled={isLocked}
                                  className="w-12 text-center bg-[#09090b] border-emerald-500/20 text-emerald-400 h-6 text-[10px] px-1 disabled:opacity-60" />
                                {scontoCanale > 0 && (
                                  <>
                                    <span className="text-zinc-600 text-[10px]">+</span>
                                    <span className="text-cyan-400 text-[10px] font-mono bg-cyan-500/10 px-1.5 py-0.5 rounded">C:{scontoCanale}</span>
                                  </>
                                )}
                                <span className="text-zinc-600 text-[10px] ml-2">|</span>
                                <span className="text-fuchsia-500/70 text-[10px]">Om.</span>
                                <EditableCell value={item.qty_omaggio || 0} decimals={3} placeholder="0"
                                  onChange={(v) => updateItem(idx, "qty_omaggio", Math.max(0, v))}
                                  className="w-14 text-center bg-[#09090b] border-fuchsia-500/30 text-fuchsia-400 h-6 text-[10px] px-1" />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`item-note-btn-${idx}`}
                                  onClick={() => setNoteOpenForIdx(noteOpenForIdx === idx ? null : idx)}
                                  className={`h-6 w-6 ml-1 ${item.note ? "text-amber-300 hover:text-amber-200" : "text-zinc-500 hover:text-zinc-300"}`}
                                  title={item.note ? "Modifica nota riga" : "Aggiungi nota riga"}
                                >
                                  <StickyNote className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {(noteOpenForIdx === idx || (item.note && item.note.length > 0)) && (
                            <tr className={`border-b border-white/5 ${rowBg}`}>
                              <td colSpan={6} className="pb-2 pt-0 px-1">
                                <Textarea
                                  data-testid={`item-note-textarea-${idx}`}
                                  value={item.note || ""}
                                  onChange={(e) => updateItem(idx, "note", e.target.value)}
                                  placeholder="Nota per questa riga (es. consegna refrigerata, lotto specifico…)"
                                  rows={2}
                                  className="bg-[#09090b] border-amber-500/30 text-amber-200 placeholder:text-zinc-600 text-xs resize-none"
                                />
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {form.items.length === 0 && (
                  <p className="text-zinc-500 text-sm text-center py-6 order-1">
                    Cerca e aggiungi prodotti all&apos;ordine
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-[#18181b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Note</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  data-testid="order-notes"
                  value={form.note}
                  onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="Note aggiuntive..."
                  rows={3}
                  className="w-full bg-[#09090b] border border-white/10 rounded-lg text-white placeholder:text-zinc-500 p-3 text-sm resize-none focus:outline-none focus:border-amber-500/50"
                />
              </CardContent>
            </Card>
          </div>

          {/* Right: Summary */}
          <div>
            <Card className="bg-[#18181b] border-white/5 sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Riepilogo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-zinc-400">
                    <span>Articoli</span>
                    <span className="text-white">{form.items.length}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400">
                    <span>Merce</span>
                    <span className="text-white font-mono">{subtotalMerce.toFixed(2)}</span>
                  </div>
                  {scontoCanale > 0 && (
                    <div className="text-cyan-400 text-xs">
                      (incl. Sc. Cliente {scontoCanale}%)
                    </div>
                  )}
                  <div className="flex justify-between text-zinc-400">
                    <span>IVA</span>
                    <span className="text-white font-mono">{ivaTotal.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between">
                    <span className="text-white font-medium">Totale</span>
                    <span className="text-amber-400 font-mono font-bold text-lg">{total.toFixed(2)} EUR</span>
                  </div>
                </div>

                <Button
                  data-testid="save-order-btn"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  onClick={handleSave}
                  disabled={saving || !form.client_name || form.items.length === 0}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Salvataggio..." : isEdit ? "Aggiorna Ordine" : "Crea Ordine"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
